<template>
    <div class="shifts-root">
    <div class="shift-cards-block">
        <div class="shift-cards-head">
            <span class="shift-cards-title">Смены дня · {{ store.currentShifts.length }}</span>
            <button
                type="button"
                class="shift-cards-add"
                :class="{ 'shift-cards-add-blocked': isAddShiftBlockedByDayStateDocument }"
                :aria-disabled="isAddShiftBlockedByDayStateDocument"
                @click="addTab"
            >
                <icon width="14" height="14" :vb-size="21"><IconPlus /></icon>Добавить
            </button>
        </div>
        <div v-if="shiftCards.length" role="tablist" aria-label="Смены дня" class="shift-cards">
            <button
                v-for="card in shiftCards"
                :key="card.index"
                type="button"
                role="tab"
                class="shift-card"
                :class="{ 'shift-card-selected': card.index === currentShiftIndex }"
                :style="card.background ? { backgroundColor: card.background } : undefined"
                :aria-selected="card.index === currentShiftIndex"
                :tabindex="card.index === currentShiftIndex ? 0 : -1"
                @click="switchTab(card.index)"
                @keydown="onCardKeydown($event, card.index)"
            >
                <span v-if="card.kind === 'extra'" class="shift-card-label shift-card-label-extra">Доп.</span>
                <span v-else class="shift-card-label">{{ card.kind === 'dayState' ? 'Кадровое состояние' : 'Основная' }}</span>
                <span class="shift-card-main" :class="{ 'shift-card-main-day-state': card.kind === 'dayState' }">{{ card.title }}</span>
                <span v-if="card.subtitle" class="shift-card-sub">{{ card.subtitle }}</span>
                <span v-if="card.hasViolation" class="shift-card-dot" aria-label="Есть нарушение" />
            </button>
        </div>
    </div>
    <!-- Ключ по смене: после удаления первой форма следующей создаётся заново, а не остаётся от удалённой. -->
    <div v-for="(shift, index) in store.currentShifts" :key="shift?.id ?? index">
        <ShiftsItem ref="shiftItemRef" :shift :schedule :day-types="dayTypes" :day-shifts="effectiveCurrentShifts"
            v-if="index == currentShiftIndex && shift" />
    </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Schedule } from '@/types'
import ShiftsItem from './ShiftsItem.vue'
import dayjs from 'dayjs'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from 'vue-toast-notification'
import { parseDurationToDecimalHours } from '@/utils/duration'
import IconPlus from '@/ui/icons/IconPlus.vue'
import { dayStateColor, describeShiftTiming, useWallClockNow } from './weekSchedule'

const store = useScheduleStore()
const authStore = useAuthStore()

const toast = useToast({ position: 'bottom-right' })

interface Props {
    date: string,
    schedule: Schedule
}

const { schedule, date } = defineProps<Props>()

const emit = defineEmits(['switch'])
// Общий минутный тикер RD-137/138 (один интервал на страницу), а не таймер на карточку.
const now = useWallClockNow()

const currentShiftIndex = ref<number>(0)
const pendingShiftIndex = ref<number | null>(null)
const shiftItemRef = ref()
// Use in schedule only assignable DayTypes
const dayTypes = computed(() => store.dayTypes.filter(item => item.assignable))
const effectiveCurrentShifts = computed(() => {
    return store.currentShifts.map((shift, index) => {
        if (index !== currentShiftIndex.value) return shift
        if (!store.currentShift) return shift

        return store.currentShift
    })
})

const sortedShiftTabs = computed(() => {
    return effectiveCurrentShifts.value
        .map((shift, index) => {
            const priority = shift.day_state_document ? 0 : shift.is_overtime ? 2 : 1
            const planStart = shift.plan_work_start_at
            const start = planStart ? dayjs.utc(planStart) : null

            return { index, priority, start: start?.isValid() ? start.valueOf() : Infinity }
        })
        .sort((left, right) => (left.priority - right.priority) || (left.start - right.start) || (left.index - right.index))
})

// Карточки в прежнем порядке sortedShiftTabs: КС, основная, дополнительные; внутри — по началу плана.
const shiftCards = computed(() => sortedShiftTabs.value.map(({ index }) => {
    const shift = effectiveCurrentShifts.value[index]
    const dayType = store.dayTypes.find(item => item.id === shift?.day_type)
    if (shift?.day_state_document) {
        // Период документа в данных графика не приходит (только ID) — не выдумываем.
        return { index, kind: 'dayState' as const, title: dayType?.name || 'Кадровое отклонение', subtitle: '', background: dayStateColor(dayType?.color), hasViolation: false }
    }
    const timing = describeShiftTiming(shift, now.value, undefined)
    const period = timing.planStart && timing.planEnd ? `${timing.planStart}–${timing.planEnd}` : (dayType?.name ?? '')
    return {
        index,
        kind: shift?.is_overtime ? 'extra' as const : 'main' as const,
        title: period,
        subtitle: shift?.position ? store.getPosition(shift.position)?.name ?? '' : '',
        background: '',
        hasViolation: timing.violationCount > 0 || timing.hasGeneralViolation,
    }
}))

// Стрелки переводят фокус между карточками; выбор — Enter/Space (с прежним подтверждением при dirty).
const onCardKeydown = (event: KeyboardEvent, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    const order = shiftCards.value.map(card => card.index)
    const position = order.indexOf(index)
    const next = order[(position + (event.key === 'ArrowRight' ? 1 : order.length - 1)) % order.length]
    const target = (event.currentTarget as HTMLElement).parentElement?.children[order.indexOf(next)] as HTMLElement | undefined
    event.preventDefault()
    target?.focus()
}

const hasDayStateDocumentInCurrentDay = computed(() => {
    return effectiveCurrentShifts.value.some((shift) => Boolean(shift?.day_state_document))
})
const isAddShiftBlockedByDayStateDocument = computed(() => {
    return effectiveCurrentShifts.value.some((shift) => {
        if (!shift?.day_state_document) return false

        return parseDurationToDecimalHours(shift.duration) > 0
    })
})

const addTab = () => {
    if (isAddShiftBlockedByDayStateDocument.value) return

    if(authStore.permissions?.can_create_schedule) {
        let overtime = false
    const fallbackStart = dayjs.utc(`${date}T09:00:00`)
    const fallbackEnd = dayjs.utc(`${date}T18:00:00`)
    if (!fallbackStart.isValid() || !fallbackEnd.isValid()) {
        toast.error('Некорректная дата смены')
        return
    }
    let start = fallbackStart.toISOString()
    let end = fallbackEnd.toISOString()
    // set start time of new shift is after 1 hour of last shift 
    if (store.currentShifts.length) {
        overtime = true
        const validPlanEnds = store.currentShifts
            .map((item) => item.plan_work_end_at)
            .filter((value): value is string => Boolean(value && dayjs.utc(value).isValid()))

        if (validPlanEnds.length) {
            const maxEnd = validPlanEnds.reduce((latest, current) => (
                dayjs.utc(current).isAfter(dayjs.utc(latest)) ? current : latest
            ))
            const nextStart = dayjs.utc(maxEnd)
            const nextEnd = dayjs.utc(nextStart).add(1, 'hour')
            if (!nextStart.isValid() || !nextEnd.isValid()) {
                toast.error('Некорректное время смены')
                return
            }
            start = nextStart.toISOString()
            end = nextEnd.toISOString()
        }
    } else {
        const defaultStart = dayjs.utc(`${date}T${store.currentDepartment?.opening_time || '09:00:00'}`)
        const defaultEnd = dayjs.utc(`${date}T${store.currentDepartment?.closing_time || '18:00:00'}`)

        if (!defaultStart.isValid() || !defaultEnd.isValid()) {
            toast.error('Некорректное время смены')
            return
        }

        start = defaultStart.toISOString()
        end = defaultEnd.toISOString()
    }
    if (hasDayStateDocumentInCurrentDay.value) {
        overtime = true
    }
    const shift = {
        department: schedule.department.id,
        employment: schedule.id,
        day_type: dayTypes.value[0].id,
        position: schedule.position.id,
        plan_work_start_at: start,
        plan_work_end_at: end,
        fact_work_start_at: null,
        fact_work_end_at: null,
        is_overtime: overtime,
        date: date
    }
    store.addCurrentShift(shift)
    switchTab(store.currentShifts.length - 1)
    store.handleSaveButtonAvailability()
    } else {
        toast.error('У вас нет прав на данное действие')
    }
}

const switchTab = (index: number) => {
    if (index === currentShiftIndex.value) return

    if(store.isCurrentShiftChanged || 
    (store.currentShift && !store.currentShift?.id)
) {
        pendingShiftIndex.value = index
        emit('switch')
    } else {
        setCurrentShiftIndex(index)
    }
}

const setCurrentShiftIndex = (index: number) => {
    currentShiftIndex.value = index
    store.setCurrentShift(store.currentShifts[index] ?? null)
}

const completePendingSwitch = () => {
    if (pendingShiftIndex.value === null) return

    setCurrentShiftIndex(pendingShiftIndex.value)
    pendingShiftIndex.value = null
}

const cancelPendingSwitch = () => {
    pendingShiftIndex.value = null
}

const validateCurrentShiftPlanTime = () => {
    const shiftItem = Array.isArray(shiftItemRef.value) ? shiftItemRef.value[0] : shiftItemRef.value

    return shiftItem?.validatePlanTime() ?? true
}

const deleteShiftFromCurrentShifts = () => {
    store.currentShifts.splice(currentShiftIndex.value, 1)
    // Индекс 0 остаётся прежним и switchTab его пропустит: выбор всё равно обновляется, иначе выбранной останется удалённая смена.
    if(currentShiftIndex.value === 0) setCurrentShiftIndex(0)
    else switchTab(currentShiftIndex.value - 1)
}

const onShiftDelete = async () => {
    const shift = store.currentShifts[currentShiftIndex.value]
    if(shift.id) {
        const res = await store.shiftDelete(shift)
        if(res === 'ok') {
            // Правки удалённой смены применять не к чему: окно «Сохранить изменения?» не нужно.
            store.removeChangedShiftId(shift.id)
            store.isCurrentShiftChanged = false
            deleteShiftFromCurrentShifts()
            await store.fetchStats()
        }
    } else {
        deleteShiftFromCurrentShifts()
    }
    
}

defineExpose({
    completePendingSwitch,
    cancelPendingSwitch,
    validateCurrentShiftPlanTime,
    // Удаление перенесено в футер редактора (RD-140); логика прежняя.
    deleteCurrentShift: onShiftDelete
})

</script>

<style scoped>
.shifts-root {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.shift-cards-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.shift-cards-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.shift-cards-title {
    font-size: 12px;
    color: var(--color-text-muted);
}

.shift-cards-add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    padding: 0;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-brand-main);
    cursor: pointer;
}

.shift-cards-add :deep(path) {
    stroke: currentColor;
}

.shift-cards-add-blocked {
    opacity: .6;
    cursor: not-allowed;
}

.shift-cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.shift-card {
    position: relative;
    box-sizing: border-box;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 7px 10px;
    border: 1px solid var(--color-bg-muted);
    border-radius: 4px;
    background-color: var(--color-white, #ffffff);
    text-align: left;
    cursor: pointer;
}

.shift-card-selected {
    border: 1.5px solid var(--color-brand-main);
}

.shift-card:focus-visible,
.shift-cards-add:focus-visible {
    outline: 2px solid var(--color-focus-border);
    outline-offset: 2px;
}

.shift-card-label {
    font-size: 11px;
    color: var(--color-text-muted);
}

.shift-card-label-extra {
    padding: 0 4px;
    border-radius: 2px;
    background-color: var(--color-accent-soft);
    color: var(--color-brand-main);
}

.shift-card-main {
    max-width: 100%;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-brand-main);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.shift-card-main-day-state {
    color: var(--color-text-main);
}

.shift-card-sub {
    max-width: 100%;
    font-size: 11px;
    color: var(--color-text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.shift-card-dot {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background-color: var(--color-warning);
}
</style>
